import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { bridgeUserActionTypes, initActionTypes } from '@makemydeal/dr-shared-store';
import { Actions, getDealXgId, getDealXgVersion } from '@makemydeal/dr-sales-checkin-store';
import { SalesViewMode } from '@makemydeal/dr-sales-checkin-types';
import { sessionUtils } from '@makemydeal/dr-shared-ui-utils';

const setSessionAttribute = (name: string, value: string) => {
    try {
        window.newrelic?.setCustomAttribute(name, value);
    } catch {
        // silently fail if New Relic isn't loaded
    }
};

const setDealerAttributes = (state: any) => {
    const dealer = state.dealer || {};
    const dealerMMDId = dealer.dealerMMDId ?? '';
    const dealerName = dealer.dealerName ?? '';
    const commonOrgId = dealer.commonOrgId ?? '';

    setSessionAttribute('sc_dealerMMDId', String(dealerMMDId));
    setSessionAttribute('sc_dealerName', String(dealerName));
    setSessionAttribute('sc_commonOrgId', String(commonOrgId));
};

const setDealAttribute = (state: any) => {
    const dealXgId = getDealXgId(state);
    const dealXgVersion = getDealXgVersion(state);

    if (dealXgId) {
        setSessionAttribute('sc_dealXgId', dealXgId);
    }
    if (dealXgVersion) {
        setSessionAttribute('sc_dealXgVersion', dealXgVersion);
    }
};

const setSalesViewModeAttributes = (state: any) => {
    const salesViewMode = state.dealer?.dealerSettings?.salesViewMode ?? '';
    const isActiveDealer = !!salesViewMode && salesViewMode !== SalesViewMode.Standard;

    setSessionAttribute('sc_salesViewMode', String(salesViewMode));
    setSessionAttribute('sc_isActiveDealer', String(isActiveDealer));
};

const setSessionIdAttribute = () => {
    setSessionAttribute('sc_sessionId', sessionUtils.getAppSessionId());
};

const setBridgeUserAttribute = (state: any) => {
    const bridgeUserId = state.bridgeUser?.id ?? '';
    setSessionAttribute('sc_bridgeUserId', bridgeUserId);
};

export const newRelicSessionAttributesMiddleware = (store: ReduxStore<any>) => (next: ReduxNext) => (action: AnyFSA) => {
    const result = next(action);

    try {
        const state = store.getState();

        switch (action.type) {
            case bridgeUserActionTypes.BRIDGE_USER_SUCCESS: {
                setBridgeUserAttribute(state);
                setDealerAttributes(state);
                setDealAttribute(state);
                setSessionIdAttribute();
                break;
            }
            case Actions.INITIAL_DEALER_CONFIG_SUCCESS:
            case Actions.GET_DETAILS_SUCCESS: {
                setDealerAttributes(state);
                setSalesViewModeAttributes(state);
                setSessionIdAttribute();
                break;
            }
            case Actions.FETCH_DEAL_SUCCESS: {
                setDealAttribute(state);
                setSessionIdAttribute();
                break;
            }
            case initActionTypes.INIT_PENCIL_SUCCESS: {
                setDealerAttributes(state);
                setDealAttribute(state);
                setSalesViewModeAttributes(state);
                setSessionIdAttribute();
                break;
            }
            default:
                break;
        }
    } catch {
        // silently fail if New Relic isn't loaded or any error occurs
    }

    return result;
};
